import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { ApplicationAuthorizationActionType, MagicLinkFlowLabel } from '~shared/modules/application-event/constants';
import { AuthorizationApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationEventService } from '~shared/modules/application-event/services';

import { AuthService } from './authorization-idam.service';
import { IdamRegisterTrackingService } from './idam-register-tracking.service';
import { TierOneRegistrationPromptService } from './tier.one.registration.prompt.service';
import {
  AUTHORIZATION_NOTIFICATIONS,
  FF_GA_PASSWORDLESS_SIGNUP_BUTTON_CLICK,
  FF_MAGIC_LINK_AUTH,
  taxIDConflictError,
} from '../constants/authorization.constant';
import { CSSHttpError } from '~shared/modules/error-handler/models/css-error.model';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ACCOUNT_ADD_BUSINESS_DETAILS_LINK,
  ApiConfig,
  APP_USER_PASSWORDLESS_ADD_BUSINESS_DETAILS_ROUTE,
  APP_USER_SECURE_LOGIN_NOTICE_ROUTE,
  FeatureToggleEnum,
  NUMBER_0,
  NUMBER_1,
  NUMBER_10000,
  ORDER_STEP_ADDRESS_LINK,
} from '~shared/constants';
import { isPlatformServer } from '@angular/common';
import { catchError, delayWhen, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Observable, of, throwError, timer } from 'rxjs';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { DEFAULT_DELAY_BEFORE_REDIRECT_IF_EMAIL_EXIST } from '../constants/validate-email.constant';
import { HttpClient } from '@angular/common/http';
import { COUNTRY_WITH_VAT_NUMBER_VALIDATION } from '../constants/registration-tier-two-constants';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import { TranslateService } from '@ngx-translate/core';
import { BusinessStatus, VatNumberErrors } from '../constants/business-status.constant';
import { MmAlertMode, MmAlertService, MmAlertSize, MmAlertType } from '@metromarkets/components';
import { ErrorsUtil } from '~shared/utils/errors.util';

export const TIER2_PASSWORDLESS_PAYLOAD_KEY = 'TIER2_PASSWORDLESS_PAYLOAD_KEY';
export const TIER2_PASSWORDLESS_EXTRAS_KEY = 'TIER2_PASSWORDLESS_EXTRAS_KEY';
export const MAGIC_LINK_BAG_KEY = 'magic-link-bag';
export const GA_EVENT_BATCH_SUBMIT_PREFIX = 'Passwordless Tier 2 batch submit | ';

@Injectable({
  providedIn: 'root',
})
export class PasswordlessService {
  constructor(
    private authService: AuthService,
    private tierOneRegistrationPromptService: TierOneRegistrationPromptService,
    private applicationEventService: ApplicationEventService,
    private idamRegisterTrackingService: IdamRegisterTrackingService,
    private activeFeatureToggleService: ActiveFeaturesConfigcatService,
    private router: Router,
    private route: ActivatedRoute,
    private httpClient: HttpClient,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platform: Object,
    private translateService: TranslateService,
    private mmAlertService: MmAlertService
  ) {}

  public tierOneSignupForPasswordLessFlow(
    email: string,
    isPasswordless: boolean,
    isBusiness: boolean,
    isTierThreeSignupForPasswordLessFlow: boolean,
    isBatchTier2Registration?: boolean,
    onEnd?: () => void,
    tier2PasswordlessRedirectionRoute?: string
  ): Observable<any> {
    // The parameters are email, password, isBusinessAccount, isPasswordlessUser, isTierThreeSignupForPasswordLessFlow
    const currentEmail = email?.trim();

    if (!currentEmail) {
      this.idamRegisterTrackingService.idamSignUpErrorEventTracking(
        'No email provided for Password-less Tier 1 registration'
      );
      onEnd?.();
      return of(null);
    }

    return this.authService
      .register(
        currentEmail,
        null,
        isBusiness,
        isPasswordless,
        isTierThreeSignupForPasswordLessFlow,
        isBatchTier2Registration,
        tier2PasswordlessRedirectionRoute
      )
      .pipe(
        tap(() => {
          // idamSignUpEventTracking argument is false for Personal Context, true for Business Context
          this.idamRegisterTrackingService.idamSignUpEventTracking(isBusiness, true);

          // Display the alert after successful signup
          this.tierOneRegistrationPromptService.promptSuccessFulRegistrationMessage();
        }),
        catchError((error: CSSHttpError) => {
          onEnd?.();
          //Send GA event for api error
          this.handleRegistrationFail(error, email);
          return throwError(error);
        })
      );
  }

  public fireGAEventBasedOnRegistrationType(registrationType) {
    this.applicationEventService.publishIfFFEnabled(
      FF_GA_PASSWORDLESS_SIGNUP_BUTTON_CLICK,
      new AuthorizationApplicationEvent(ApplicationAuthorizationActionType.PasswordLessSignupClick, registrationType),
      true
    );
  }

  public isOnPasswordlessBusinessDetailsPage(): boolean {
    return this.router.url.split('?')[NUMBER_0]?.endsWith(APP_USER_PASSWORDLESS_ADD_BUSINESS_DETAILS_ROUTE);
  }

  public batchTier2SubmitPhase1(tier2Payload: Record<string, any>, tier2Extras: Record<string, any>) {
    localStorage.setItem(TIER2_PASSWORDLESS_PAYLOAD_KEY, JSON.stringify(tier2Payload));
    localStorage.setItem(TIER2_PASSWORDLESS_EXTRAS_KEY, JSON.stringify(tier2Extras));

    this.route.queryParams
      .pipe(
        filter((params) => !!params?.secureLoggedEmail),
        take(NUMBER_1),
        switchMap((params) => this.tierOneSignupForPasswordLessFlow(params.secureLoggedEmail, true, false, false, true))
      )
      .subscribe();
  }

  public batchTier2SubmitPhase2(onError?: () => void) {
    if (isPlatformServer(this.platform)) {
      return;
    }

    const tier2PayloadStringified = localStorage.getItem(TIER2_PASSWORDLESS_PAYLOAD_KEY);
    const tier2ExtrasStringified = localStorage.getItem(TIER2_PASSWORDLESS_EXTRAS_KEY);
    localStorage.removeItem(TIER2_PASSWORDLESS_PAYLOAD_KEY);
    localStorage.removeItem(TIER2_PASSWORDLESS_EXTRAS_KEY);

    if (tier2PayloadStringified && tier2ExtrasStringified) {
      const tier2Payload = JSON.parse(tier2PayloadStringified);
      const tier2Extras = JSON.parse(tier2ExtrasStringified);
      this.authService
        .registerTierTwoAccount(tier2Payload, tier2Extras.saveShippingAndBilling, {
          saveLegalAddressAsShippingAndBilling: tier2Extras.saveLegalAddressAsShippingAndBilling,
          backUrl: ORDER_STEP_ADDRESS_LINK.substring(NUMBER_1),
        })
        .pipe(
          take(NUMBER_1),
          catchError((err) => {
            if (err.invalidParams) {
              this.handleValidationEventFromAPI(err, GA_EVENT_BATCH_SUBMIT_PREFIX);
            }
            this.router
              .navigate([ACCOUNT_ADD_BUSINESS_DETAILS_LINK], {
                state: { passwordlessTier2: 'true' },
              })
              .then(() => this.handleBusinessRegistrationFail(err));
            return throwError(err);
          })
        )
        .subscribe(() => {
          this.idamRegisterTrackingService.idamSignUpEventTracking(true, true);
          this.router.navigate([ORDER_STEP_ADDRESS_LINK]);
        }, onError);
    }
  }

  public prepareMagicLinkSetup(magicLinkBag: { email: string; backUrl: string; expirationRoute: string }) {
    localStorage.setItem(MAGIC_LINK_BAG_KEY, JSON.stringify(magicLinkBag));
  }

  public triggerMagicLink() {
    const magicLinkBagStr = localStorage.getItem(MAGIC_LINK_BAG_KEY);
    if (magicLinkBagStr) {
      const magicLinkBag = JSON.parse(magicLinkBagStr);
      this.authService
        .sendMagicLink(magicLinkBag)
        .pipe(
          map(({ isEmailSent }) => isEmailSent === true),
          catchError(() => of(false))
        )
        .subscribe((shouldProceedWithMagicFlow) => {
          const errorInMagicFlow = !shouldProceedWithMagicFlow;

          if (errorInMagicFlow) {
            this.applicationEventService.publishIfFFEnabled(
              FF_MAGIC_LINK_AUTH,
              new AuthorizationApplicationEvent(
                ApplicationAuthorizationActionType.MagicLinkFlow,
                MagicLinkFlowLabel.Failed
              )
            );
          }

          if (shouldProceedWithMagicFlow) {
            this.applicationEventService.publishIfFFEnabled(
              FF_MAGIC_LINK_AUTH,
              new AuthorizationApplicationEvent(
                ApplicationAuthorizationActionType.MagicLinkFlow,
                MagicLinkFlowLabel.Success
              )
            );

            localStorage.removeItem(MAGIC_LINK_BAG_KEY);
            this.router.navigate([APP_USER_SECURE_LOGIN_NOTICE_ROUTE], {
              state: {
                email: magicLinkBag.email,
                backUrl: magicLinkBag.backUrl,
              },
            });
          } else {
            this.normalLoginFlow(magicLinkBag.email, magicLinkBag.backUrl);
          }
        });
    }
  }

  public normalLoginFlow(email: string, backUrl: string) {
    return this.activeFeatureToggleService
      .getFeatureValue(FeatureToggleEnum.FF_DELAY_BEFORE_REDIRECT, DEFAULT_DELAY_BEFORE_REDIRECT_IF_EMAIL_EXIST)
      .pipe(delayWhen((delayTime) => timer(Number(delayTime))))
      .subscribe(() => this.authService.login(backUrl, email));
  }

  getIsUserPasswordless(): Observable<{ passwordless: boolean }> {
    const url = `${ApiConfig.SVC_BUYER_ACCOUNT_API_URL()}/passwordless-check`;
    return this.httpClient.get<{ passwordless: boolean }>(url);
  }

  handleValidationEventFromAPI(error: CSSHttpError, prefix = '') {
    const errors = ErrorsUtil.collectFormAPIErrors(error);
    const message = ErrorsUtil.concatErrorsToString(errors, prefix);
    this.idamRegisterTrackingService.idamSignUpErrorEventTracking(message);
  }

  handleBusinessRegistrationFail(httpError): void {
    const error = httpError?.error?.error;
    const message = httpError?.error?.message;
    const { invalidParams } = httpError;
    return error === taxIDConflictError
      ? this.handleTaxConflictAlert(error, message)
      : this.handleInvalidVatNumber(invalidParams);
  }

  private handleRegistrationFail(cssHttpError: CSSHttpError, email: string): void {
    const message = `{ ${cssHttpError?.message.replace(email, '*****')}}`;
    this.idamRegisterTrackingService.idamSignUpErrorEventTracking(message);
  }

  private handleInvalidVatNumber(invalidParams): void {
    let VatNumberNotValidAlert = '';
    let VatNumberNotValidTitleAlert = '';

    if (invalidParams?.vatNumber?.type === BusinessStatus.blocked) {
      // split payment
      VatNumberNotValidAlert = VatNumberErrors.VatNumberBlockedAlert;
      VatNumberNotValidTitleAlert = VatNumberErrors.VatNumberBlockedTitleAlert;
    } else {
      VatNumberNotValidAlert = VatNumberErrors.VatNumberNotValidAlert;
      VatNumberNotValidTitleAlert = VatNumberErrors.VatNumberNotValidTitleAlert;
    }

    this.translateService.get([VatNumberNotValidAlert, VatNumberNotValidTitleAlert]).subscribe((translations) => {
      this.mmAlertService.addAlert({
        type: MmAlertType.error,
        title: translations[VatNumberNotValidAlert],
        message: translations[VatNumberNotValidTitleAlert],
        mode: MmAlertMode.banner,
        size: MmAlertSize.large,
        duration: NUMBER_10000,
      });
    });
  }

  private handleTaxConflictAlert(error: string, message: string): void {
    if (COUNTRY_WITH_VAT_NUMBER_VALIDATION[MarketDetectionUtil.getCountryCode()]) {
      error = 'VATNUMBERNOTUNIQUE';
    }

    const label =
      message === 'Conflict'
        ? `{ ${GA_EVENT_BATCH_SUBMIT_PREFIX}${error} }`
        : `{ ${GA_EVENT_BATCH_SUBMIT_PREFIX}${message} }`;
    this.idamRegisterTrackingService.idamSignUpErrorEventTracking(label);

    const taxNumberNotUniqueAlert = `${AUTHORIZATION_NOTIFICATIONS.submitError}${error && error.toUpperCase()}`;
    const taxNumberNotUniqueError = `PAGES.AUTH.ADD_BUSINESS_DETAILS.ERROR.${error && error.toUpperCase()}`;
    this.translateService.get([taxNumberNotUniqueAlert, taxNumberNotUniqueError]).subscribe((translations) => {
      this.mmAlertService.addAlert({
        type: MmAlertType.error,
        title: translations[taxNumberNotUniqueAlert],
        message: translations[taxNumberNotUniqueError],
        mode: MmAlertMode.banner,
        size: MmAlertSize.large,
        duration: NUMBER_10000,
      });
    });
  }
}
